import { PDFDocument, rgb, StandardFonts, degrees } from "pdf-lib";

export const PAGE_SIZES = {
  a4: { width: 595.28, height: 841.89, label: "A4" },
  letter: { width: 612, height: 792, label: "Letter" },
  legal: { width: 612, height: 1008, label: "Legal" },
  a5: { width: 419.53, height: 595.28, label: "A5" },
  square: { width: 612, height: 612, label: "Square" },
  widescreen: { width: 842, height: 474, label: "Widescreen" },
};

const FONT_MAP = {
  helvetica: {
    regular: StandardFonts.Helvetica,
    bold: StandardFonts.HelveticaBold,
    italic: StandardFonts.HelveticaOblique,
    boldItalic: StandardFonts.HelveticaBoldOblique,
  },
  times: {
    regular: StandardFonts.TimesRoman,
    bold: StandardFonts.TimesRomanBold,
    italic: StandardFonts.TimesRomanItalic,
    boldItalic: StandardFonts.TimesRomanBoldItalic,
  },
  courier: {
    regular: StandardFonts.Courier,
    bold: StandardFonts.CourierBold,
    italic: StandardFonts.CourierOblique,
    boldItalic: StandardFonts.CourierBoldOblique,
  },
};

const hexToRgb = (hex) => {
  const h = (hex || "#000000").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return {
    r: ((n >> 16) & 255) / 255,
    g: ((n >> 8) & 255) / 255,
    b: (n & 255) / 255,
  };
};

const toPdfY = (pageHeight, yNorm, hNorm = 0) => pageHeight * (1 - yNorm - hNorm);

async function embedFonts(pdfDoc) {
  const cache = {};
  for (const [family, variants] of Object.entries(FONT_MAP)) {
    cache[family] = {};
    for (const [style, std] of Object.entries(variants)) {
      cache[family][style] = await pdfDoc.embedFont(std);
    }
  }
  return cache;
}

function pickFont(fonts, ann) {
  const family = fonts[ann.fontFamily || "helvetica"] || fonts.helvetica;
  if (ann.bold && ann.italic) return family.boldItalic;
  if (ann.bold) return family.bold;
  if (ann.italic) return family.italic;
  return family.regular;
}

/**
 * Create a brand-new PDF (blank or with a light template).
 * template: 'blank' | 'lined' | 'dots' | 'checklist' | 'invoice'
 */
export async function createBlankPdf(sizeKey = "a4", template = "blank", options = {}) {
  const size = PAGE_SIZES[sizeKey] || PAGE_SIZES.a4;
  const landscape = !!options.landscape;
  const width = landscape ? size.height : size.width;
  const height = landscape ? size.width : size.height;

  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage([width, height]);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const ink = rgb(0.78, 0.82, 0.88);
  const accent = rgb(0.23, 0.56, 0.77);

  if (template === "lined") {
    const gap = 28;
    for (let y = height - 72; y > 48; y -= gap) {
      page.drawLine({
        start: { x: 48, y },
        end: { x: width - 48, y },
        thickness: 0.6,
        color: ink,
        opacity: 0.7,
      });
    }
    page.drawLine({
      start: { x: 72, y: 48 },
      end: { x: 72, y: height - 48 },
      thickness: 1,
      color: rgb(0.9, 0.45, 0.45),
      opacity: 0.55,
    });
  }

  if (template === "dots") {
    for (let y = 48; y < height - 40; y += 22) {
      for (let x = 48; x < width - 40; x += 22) {
        page.drawCircle({
          x,
          y,
          size: 1.1,
          color: ink,
          opacity: 0.75,
        });
      }
    }
  }

  if (template === "checklist") {
    page.drawText(options.title || "Checklist", {
      x: 48,
      y: height - 56,
      size: 22,
      font: fontBold,
      color: rgb(0.1, 0.18, 0.28),
    });
    page.drawLine({
      start: { x: 48, y: height - 68 },
      end: { x: width - 48, y: height - 68 },
      thickness: 1.5,
      color: accent,
    });
    let y = height - 110;
    for (let i = 0; i < 12; i++) {
      page.drawRectangle({
        x: 52,
        y: y - 2,
        width: 14,
        height: 14,
        borderColor: accent,
        borderWidth: 1.2,
      });
      page.drawLine({
        start: { x: 78, y: y + 4 },
        end: { x: width - 52, y: y + 4 },
        thickness: 0.5,
        color: ink,
      });
      y -= 36;
    }
  }

  if (template === "invoice") {
    page.drawText(options.title || "INVOICE", {
      x: 48,
      y: height - 56,
      size: 26,
      font: fontBold,
      color: accent,
    });
    page.drawText("From / De:", { x: 48, y: height - 100, size: 10, font, color: rgb(0.4, 0.45, 0.5) });
    page.drawText("Bill to / Facturar a:", {
      x: width / 2,
      y: height - 100,
      size: 10,
      font,
      color: rgb(0.4, 0.45, 0.5),
    });
    page.drawRectangle({
      x: 48,
      y: height - 280,
      width: width - 96,
      height: 28,
      color: rgb(0.9, 0.94, 0.97),
    });
    ["Description", "Qty", "Price", "Total"].forEach((label, i) => {
      page.drawText(label, {
        x: 56 + i * ((width - 110) / 4),
        y: height - 270,
        size: 10,
        font: fontBold,
        color: rgb(0.2, 0.25, 0.3),
      });
    });
    for (let r = 0; r < 8; r++) {
      const y = height - 310 - r * 28;
      page.drawLine({
        start: { x: 48, y },
        end: { x: width - 48, y },
        thickness: 0.4,
        color: ink,
      });
    }
  }

  return pdfDoc.save();
}

export async function insertBlankPage(originalBytes, afterIndex, sizeKey = "a4") {
  const src = await PDFDocument.load(originalBytes, { ignoreEncryption: true });
  const out = await PDFDocument.create();
  const size = PAGE_SIZES[sizeKey] || PAGE_SIZES.a4;
  const count = src.getPageCount();

  if (count === 0) {
    out.addPage([size.width, size.height]);
    return out.save();
  }

  for (let i = 0; i < count; i++) {
    const [copied] = await out.copyPages(src, [i]);
    out.addPage(copied);
    if (i === afterIndex) {
      out.addPage([size.width, size.height]);
    }
  }
  return out.save();
}

export async function duplicatePageInPdf(originalBytes, pageIndex) {
  const src = await PDFDocument.load(originalBytes, { ignoreEncryption: true });
  const out = await PDFDocument.create();
  const count = src.getPageCount();
  for (let i = 0; i < count; i++) {
    const [copied] = await out.copyPages(src, [i]);
    out.addPage(copied);
    if (i === pageIndex) {
      const [dup] = await out.copyPages(src, [i]);
      out.addPage(dup);
    }
  }
  return out.save();
}

export async function exportAnnotatedPdf(originalBytes, pagesMeta, annotations) {
  const src = originalBytes instanceof Uint8Array ? originalBytes : new Uint8Array(originalBytes);
  const pdfDoc = await PDFDocument.load(src.slice(0), { ignoreEncryption: true });
  const fonts = await embedFonts(pdfDoc);
  const font = fonts.helvetica.regular;
  const fontBold = fonts.helvetica.bold;

  const pageCount = pdfDoc.getPageCount();
  for (let i = 0; i < pageCount; i++) {
    const page = pdfDoc.getPage(i);
    const { width, height } = page.getSize();
    const meta = pagesMeta[i] || {};
    const rotation = meta.rotation || 0;
    if (rotation) {
      page.setRotation(degrees((page.getRotation().angle + rotation) % 360));
    }

    const pageAnns = annotations.filter((a) => a.pageIndex === i);
    for (const ann of pageAnns) {
      try {
        await drawAnnotation(page, ann, width, height, font, fontBold, pdfDoc, fonts);
      } catch (err) {
        console.warn("Annotation export skipped:", ann?.type, err);
      }
    }
  }

  return pdfDoc.save();
}

/** Standard PDF fonts only support WinAnsi — map common Latin chars. */
function toWinAnsi(text) {
  const map = {
    "\u00e1": "a",
    "\u00e0": "a",
    "\u00e4": "a",
    "\u00e2": "a",
    "\u00e3": "a",
    "\u00c1": "A",
    "\u00c0": "A",
    "\u00c4": "A",
    "\u00c2": "A",
    "\u00c3": "A",
    "\u00e9": "e",
    "\u00e8": "e",
    "\u00eb": "e",
    "\u00ea": "e",
    "\u00c9": "E",
    "\u00c8": "E",
    "\u00cb": "E",
    "\u00ca": "E",
    "\u00ed": "i",
    "\u00ec": "i",
    "\u00ef": "i",
    "\u00ee": "i",
    "\u00cd": "I",
    "\u00cc": "I",
    "\u00cf": "I",
    "\u00ce": "I",
    "\u00f3": "o",
    "\u00f2": "o",
    "\u00f6": "o",
    "\u00f4": "o",
    "\u00f5": "o",
    "\u00d3": "O",
    "\u00d2": "O",
    "\u00d6": "O",
    "\u00d4": "O",
    "\u00d5": "O",
    "\u00fa": "u",
    "\u00f9": "u",
    "\u00fc": "u",
    "\u00fb": "u",
    "\u00da": "U",
    "\u00d9": "U",
    "\u00dc": "U",
    "\u00db": "U",
    "\u00f1": "n",
    "\u00d1": "N",
    "\u00e7": "c",
    "\u00c7": "C",
    "\u00bf": "?",
    "\u00a1": "!",
    "\u2014": "-",
    "\u2013": "-",
    "\u201c": '"',
    "\u201d": '"',
    "\u2018": "'",
    "\u2019": "'",
    "\u2026": "...",
    "\u20ac": "EUR",
    "\u2022": "*",
  };
  return String(text || "")
    .split("")
    .map((ch) => {
      if (map[ch]) return map[ch];
      const code = ch.charCodeAt(0);
      if (code === 9) return " ";
      if (code === 10 || code === 13) return ch;
      if (code >= 32 && code <= 126) return ch;
      // Latin-1 printable range often works in WinAnsi
      if (code >= 160 && code <= 255) return ch;
      return "";
    })
    .join("");
}

async function drawAnnotation(page, ann, width, height, font, fontBold, pdfDoc, fonts) {
  const color = hexToRgb(ann.color || "#e74c3c");
  const opacity = ann.opacity ?? 1;
  const x = ann.x * width;
  const yTop = toPdfY(height, ann.y);
  const w = (ann.w || 0) * width;
  const h = (ann.h || 0) * height;
  const y = toPdfY(height, ann.y, ann.h || 0);
  const stroke = Math.max(0.5, (ann.strokeWidth || 2) * (width / 600));
  const textFont = pickFont(fonts, ann);

  switch (ann.type) {
    case "highlight": {
      page.drawRectangle({
        x,
        y,
        width: w,
        height: h || height * 0.025,
        color: rgb(color.r, color.g, color.b),
        opacity: ann.opacity ?? 0.35,
        borderWidth: 0,
      });
      break;
    }
    case "underline": {
      page.drawLine({
        start: { x, y: yTop - 2 },
        end: { x: x + w, y: yTop - 2 },
        thickness: stroke,
        color: rgb(color.r, color.g, color.b),
        opacity,
      });
      break;
    }
    case "strikeout": {
      const mid = y + h / 2;
      page.drawLine({
        start: { x, y: mid },
        end: { x: x + w, y: mid },
        thickness: stroke,
        color: rgb(color.r, color.g, color.b),
        opacity,
      });
      break;
    }
    case "rect": {
      page.drawRectangle({
        x,
        y,
        width: w,
        height: h,
        borderColor: rgb(color.r, color.g, color.b),
        borderWidth: stroke,
        borderOpacity: opacity,
        color: ann.filled ? rgb(color.r, color.g, color.b) : undefined,
        opacity: ann.filled ? (ann.opacity ?? 0.2) : 0,
      });
      break;
    }
    case "ellipse": {
      page.drawEllipse({
        x: x + w / 2,
        y: y + h / 2,
        xScale: Math.abs(w / 2),
        yScale: Math.abs(h / 2),
        borderColor: rgb(color.r, color.g, color.b),
        borderWidth: stroke,
        borderOpacity: opacity,
        color: ann.filled ? rgb(color.r, color.g, color.b) : undefined,
        opacity: ann.filled ? (ann.opacity ?? 0.2) : 0,
      });
      break;
    }
    case "line":
    case "arrow": {
      const x2 = (ann.x2 ?? ann.x + (ann.w || 0)) * width;
      const y2 = toPdfY(height, ann.y2 ?? ann.y + (ann.h || 0));
      page.drawLine({
        start: { x, y: yTop },
        end: { x: x2, y: y2 },
        thickness: stroke,
        color: rgb(color.r, color.g, color.b),
        opacity,
      });
      if (ann.type === "arrow") {
        const angle = Math.atan2(y2 - yTop, x2 - x);
        const head = 10 * (width / 600);
        const a1 = angle + Math.PI * 0.8;
        const a2 = angle - Math.PI * 0.8;
        page.drawLine({
          start: { x: x2, y: y2 },
          end: { x: x2 + Math.cos(a1) * head, y: y2 + Math.sin(a1) * head },
          thickness: stroke,
          color: rgb(color.r, color.g, color.b),
          opacity,
        });
        page.drawLine({
          start: { x: x2, y: y2 },
          end: { x: x2 + Math.cos(a2) * head, y: y2 + Math.sin(a2) * head },
          thickness: stroke,
          color: rgb(color.r, color.g, color.b),
          opacity,
        });
      }
      break;
    }
    case "draw": {
      const points = ann.points || [];
      for (let i = 1; i < points.length; i++) {
        const p0 = points[i - 1];
        const p1 = points[i];
        page.drawLine({
          start: { x: p0.x * width, y: toPdfY(height, p0.y) },
          end: { x: p1.x * width, y: toPdfY(height, p1.y) },
          thickness: stroke,
          color: rgb(color.r, color.g, color.b),
          opacity,
          lineCap: 1,
        });
      }
      break;
    }
    case "text":
    case "textReplace": {
      // Cover original glyphs when replacing existing PDF text
      if (ann.type === "textReplace" || ann.cover) {
        const pad = 1;
        const cover = hexToRgb(ann.coverColor || "#ffffff");
        page.drawRectangle({
          x: x - pad,
          y: y - pad,
          width: Math.max(w, 4) + pad * 2,
          height: Math.max(h, 4) + pad * 2,
          color: rgb(cover.r, cover.g, cover.b),
          borderWidth: 0,
        });
      }
      const size = Math.max(
        4,
        ann.fontSize
          ? ann.fontSize * (ann.fontSizeAbsolute ? 1 : width / 600)
          : 14 * (width / 600)
      );
      const lines = String(ann.text || "").split("\n");
      const lineH = size * (ann.lineHeight || 1.25);
      lines.forEach((line, idx) => {
        const safe = toWinAnsi(line);
        if (!safe) return;
        let drawX = x;
        if (ann.align === "center") {
          const tw = textFont.widthOfTextAtSize(safe, size);
          drawX = x + (w - tw) / 2;
        } else if (ann.align === "right") {
          const tw = textFont.widthOfTextAtSize(safe, size);
          drawX = x + w - tw;
        }
        const drawY = yTop - size - idx * lineH;
        if (drawY < -size || drawY > height + size) return;
        try {
          page.drawText(safe, {
            x: Math.max(0, drawX),
            y: drawY,
            size,
            font: textFont,
            color: rgb(color.r, color.g, color.b),
            opacity,
          });
          if (ann.underline) {
            const tw = textFont.widthOfTextAtSize(safe, size);
            page.drawLine({
              start: { x: drawX, y: drawY - 1.5 },
              end: { x: drawX + tw, y: drawY - 1.5 },
              thickness: Math.max(0.6, size * 0.06),
              color: rgb(color.r, color.g, color.b),
            });
          }
        } catch (err) {
          console.warn("drawText skipped:", err);
        }
      });
      break;
    }
    case "note": {
      const size = 10 * (width / 600);
      page.drawRectangle({
        x,
        y: yTop - 80,
        width: Math.min(w || width * 0.28, width * 0.4),
        height: 80,
        color: rgb(1, 0.95, 0.55),
        opacity: 0.95,
        borderColor: rgb(0.85, 0.7, 0.2),
        borderWidth: 1,
      });
      const noteLines = String(ann.text || "Nota").split("\n").slice(0, 5);
      noteLines.forEach((line, idx) => {
        const safe = toWinAnsi(line.slice(0, 40));
        if (!safe) return;
        page.drawText(safe, {
          x: x + 6,
          y: yTop - 18 - idx * (size + 4),
          size,
          font,
          color: rgb(0.15, 0.12, 0.05),
        });
      });
      break;
    }
    case "whiteout":
    case "redact": {
      page.drawRectangle({
        x,
        y,
        width: w,
        height: h,
        color: ann.type === "redact" ? rgb(0, 0, 0) : rgb(1, 1, 1),
        borderWidth: 0,
      });
      break;
    }
    case "image":
    case "signature": {
      if (!ann.imageBytes) break;
      let embedded;
      try {
        if (ann.imageMime === "image/png" || (ann.imageDataUrl || "").includes("image/png")) {
          embedded = await pdfDoc.embedPng(ann.imageBytes);
        } else {
          embedded = await pdfDoc.embedJpg(ann.imageBytes);
        }
      } catch {
        try {
          embedded = await pdfDoc.embedPng(ann.imageBytes);
        } catch {
          return;
        }
      }
      page.drawImage(embedded, { x, y, width: w, height: h, opacity });
      break;
    }
    case "stamp": {
      const size = 16 * (width / 600);
      const label = toWinAnsi(ann.text || "APROBADO") || "STAMP";
      const tw = fontBold.widthOfTextAtSize(label, size);
      const pad = 8;
      page.drawRectangle({
        x,
        y: yTop - size - pad * 2,
        width: tw + pad * 2,
        height: size + pad * 2,
        borderColor: rgb(color.r, color.g, color.b),
        borderWidth: 2,
        borderOpacity: opacity,
      });
      page.drawText(label, {
        x: x + pad,
        y: yTop - size - pad,
        size,
        font: fontBold,
        color: rgb(color.r, color.g, color.b),
        opacity,
      });
      break;
    }
    default:
      break;
  }
}

export async function rebuildPdfWithPageOps(originalBytes, pageOrder, deletedSet, blankInserts) {
  const src = await PDFDocument.load(originalBytes, { ignoreEncryption: true });
  const out = await PDFDocument.create();

  for (const entry of pageOrder) {
    if (deletedSet.has(entry)) continue;
    if (typeof entry === "string" && entry.startsWith("blank:")) {
      const blank = blankInserts[entry];
      out.addPage([blank?.width || 595, blank?.height || 842]);
    } else {
      const [copied] = await out.copyPages(src, [entry]);
      out.addPage(copied);
    }
  }
  return out.save();
}

export async function mergePdfFiles(fileBuffers) {
  const out = await PDFDocument.create();
  for (const bytes of fileBuffers) {
    const doc = await PDFDocument.load(bytes, { ignoreEncryption: true });
    const pages = await out.copyPages(doc, doc.getPageIndices());
    pages.forEach((p) => out.addPage(p));
  }
  return out.save();
}

export async function extractPages(originalBytes, indices) {
  const src = await PDFDocument.load(originalBytes, { ignoreEncryption: true });
  const out = await PDFDocument.create();
  const pages = await out.copyPages(src, indices);
  pages.forEach((p) => out.addPage(p));
  return out.save();
}

export async function addWatermarkToBytes(originalBytes, text, options = {}) {
  const pdfDoc = await PDFDocument.load(originalBytes, { ignoreEncryption: true });
  const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const color = hexToRgb(options.color || "#999999");
  const opacity = options.opacity ?? 0.25;

  pdfDoc.getPages().forEach((page) => {
    const { width, height } = page.getSize();
    const size = options.fontSize || Math.min(width, height) * 0.08;
    const tw = font.widthOfTextAtSize(text, size);
    page.drawText(text, {
      x: (width - tw) / 2,
      y: height / 2,
      size,
      font,
      color: rgb(color.r, color.g, color.b),
      opacity,
      rotate: degrees(options.angle ?? -35),
    });
  });
  return pdfDoc.save();
}

export async function addPageNumbersToBytes(originalBytes, format = "n") {
  const pdfDoc = await PDFDocument.load(originalBytes, { ignoreEncryption: true });
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const total = pdfDoc.getPageCount();

  pdfDoc.getPages().forEach((page, i) => {
    const { width } = page.getSize();
    const label = format === "n/N" ? `${i + 1} / ${total}` : String(i + 1);
    const size = 10;
    const tw = font.widthOfTextAtSize(label, size);
    page.drawText(label, {
      x: (width - tw) / 2,
      y: 24,
      size,
      font,
      color: rgb(0.3, 0.3, 0.3),
    });
  });
  return pdfDoc.save();
}

export function downloadBytes(bytes, filename) {
  const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  // Copy into a plain ArrayBuffer-backed view for Blob compatibility
  const copy = new Uint8Array(data.byteLength);
  copy.set(data);
  const blob = new Blob([copy.buffer], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "edited.pdf";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export async function dataUrlToBytes(dataUrl) {
  const res = await fetch(dataUrl);
  const buf = await res.arrayBuffer();
  return new Uint8Array(buf);
}
